import { PresentationEditor } from "./components/PresentationEditor"
import { CopyOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Input,
  List,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd"
import { useCallback, useMemo, useState } from "react"
import { Link, useHistory, useParams } from "react-router-dom"
import { useRegulatorySession as useAuthContext } from "../../providers/regulatoryProvider"
import type { CatalogHistory, CatalogRevision, ResolvedCatalog } from "../../types/regulatory"
import { catalogPermissions } from "../../utils/regulatory"
import { regulatoryApi } from "../../utils/regulatoryApi"
import {
  CatalogFrame,
  JsonDetails,
  OwnershipTag,
  ResourceState,
  ReviewTag,
  ScopeTag,
} from "./components/CatalogUI"
import { PointTable } from "./components/PointTable"
import { RevisionComparison } from "./components/RevisionComparison"
import { dateValue, textValue } from "./format"
import { useCatalogResource } from "./useCatalog"
function ResolvedPanel({ revision }: { revision: CatalogRevision }) {
  const resource = useCatalogResource(
    useCallback(
      (signal) => regulatoryApi.resolved(revision.templateUuid, revision.uuid, signal),
      [revision.templateUuid, revision.uuid],
    ),
  )
  return (
    <ResourceState {...resource} retry={resource.reload}>
      {resource.data && <ResolvedView value={resource.data} />}
    </ResourceState>
  )
}
function ResolvedView({ value }: { value: ResolvedCatalog }) {
  const groups = useMemo(() => {
    const names = new Map(
      value.templates.map((template) => [
        template.revisionUuid,
        `${template.name} · v${template.sourceVersion}`,
      ]),
    )
    const result: {
      key: string
      title: string
      points: ResolvedCatalog["controls"][number]["point"][]
    }[] = []
    for (const control of value.controls) {
      const key = `${control.revisionUuid}/${control.point.definition.section}`
      let group = result[result.length - 1]
      if (!group || group.key !== key) {
        group = {
          key,
          title: `${names.get(control.revisionUuid) ?? control.revisionUuid} — ${control.point.definition.section}`,
          points: [],
        }
        result.push(group)
      }
      group.points.push(control.point)
    }
    return result
  }, [value])
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <Space wrap>
        <Tag>{value.controls.length} contrôles</Tag>
        <Tag>{value.templates.length} modèles utilisés</Tag>
        <ReviewTag validated={value.allReviewed} />
      </Space>
      {(value.missingParts.length > 0 ||
        value.coverageGaps > 0 ||
        value.interpretationReserves > 0) && (
        <Alert
          type="warning"
          showIcon
          message="Couverture à compléter ou interprétations à clarifier"
          description={`${value.coverageGaps} compléments absents · ${value.interpretationReserves} réserves d’interprétation${value.missingParts.length ? ` · Parties manquantes : ${value.missingParts.join(", ")}` : ""}`}
        />
      )}
      <List
        dataSource={value.templates}
        renderItem={(template) => (
          <List.Item>
            <Space direction="vertical">
              <Link
                to={`/regulatory/templates/${template.templateUuid}/revisions/${template.revisionUuid}`}
              >
                {template.name} · v{template.sourceVersion}
              </Link>
              <Typography.Text type="secondary">{textValue(template.coverage)}</Typography.Text>
            </Space>
            <ReviewTag validated={template.validated} />
          </List.Item>
        )}
      />
      {groups.map((group, index) => (
        <Card key={`${group.key}/${index}`} title={group.title}>
          <PointTable points={group.points} />
        </Card>
      ))}
    </Space>
  )
}
function IncludesPanel({ revision }: { revision: CatalogRevision }) {
  const resource = useCatalogResource(
    useCallback(
      async (signal) =>
        Promise.all(
          revision.includes.map(async (link) => {
            if (!link.revisionUuid) return { link, name: "Module historique", version: null }
            const child = await regulatoryApi.revision(link.templateUuid, link.revisionUuid, signal)
            return { link, name: child.definition.name, version: child.sourceVersion }
          }),
        ),
      [revision.includes],
    ),
  )
  if (!revision.includes.length)
    return <Typography.Paragraph>Aucun module inclus dans cette révision.</Typography.Paragraph>
  return (
    <ResourceState {...resource} retry={resource.reload}>
      <List
        dataSource={resource.data ?? []}
        renderItem={(item, index) => (
          <List.Item>
            <Space>
              <Tag>{index + 1}</Tag>
              {item.link.revisionUuid ? (
                <Link
                  to={`/regulatory/templates/${item.link.templateUuid}/revisions/${item.link.revisionUuid}`}
                >
                  {item.name} · v{item.version}
                </Link>
              ) : (
                <span>{item.name} — révision inconnue</span>
              )}
            </Space>
            {!item.link.revisionUuid && <Tag color="orange">Lien historique non résolu</Tag>}
          </List.Item>
        )}
      />
    </ResourceState>
  )
}
function HistoryPanel({
  templateUuid,
  selected,
}: {
  templateUuid: string
  selected: CatalogRevision
}) {
  const [page, setPage] = useState(1)
  const [recordPage, setRecordPage] = useState(1)
  const revisions = useCatalogResource(
    useCallback(
      (signal) =>
        regulatoryApi.revisions(templateUuid, { limit: 25, offset: (page - 1) * 25 }, signal),
      [templateUuid, page],
    ),
  )
  const records = useCatalogResource(
    useCallback(
      (signal) =>
        regulatoryApi.history(templateUuid, { limit: 25, offset: (recordPage - 1) * 25 }, signal),
      [templateUuid, recordPage],
    ),
  )
  const [compareUuid, setCompareUuid] = useState<string>()
  const [comparison, setComparison] = useState<CatalogRevision | null>(null)
  const [compareLoading, setCompareLoading] = useState(false)
  const [compareError, setCompareError] = useState<string>()
  const compare = async () => {
    if (!compareUuid) return
    setCompareLoading(true)
    setCompareError(undefined)
    try {
      setComparison(await regulatoryApi.revision(templateUuid, compareUuid))
    } catch (error) {
      setCompareError(error instanceof Error ? error.message : "Comparaison impossible")
    } finally {
      setCompareLoading(false)
    }
  }
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <ResourceState {...revisions} retry={revisions.reload}>
        <Table<CatalogRevision>
          rowKey="uuid"
          dataSource={revisions.data?.items ?? []}
          pagination={{
            current: page,
            pageSize: 25,
            total: revisions.data?.total,
            onChange: setPage,
          }}
          columns={[
            {
              title: "Version",
              render: (_value, row) => (
                <Link to={`/regulatory/templates/${templateUuid}/revisions/${row.uuid}`}>
                  Version {row.sourceVersion}
                </Link>
              ),
            },
            { title: "Nom", render: (_value, row) => row.definition.name },
            {
              title: "Revue",
              render: (_value, row) => <ReviewTag validated={row.definition.validated === true} />,
            },
            {
              title: "Date de revue",
              render: (_value, row) => dateValue(row.definition.reviewDate),
            },
          ]}
        />
      </ResourceState>
      <Space wrap>
        <Select
          aria-label="Révision à comparer"
          placeholder="Comparer avec une révision"
          value={compareUuid}
          onChange={setCompareUuid}
          options={revisions.data?.items
            .filter((row) => row.uuid !== selected.uuid)
            .map((row) => ({ value: row.uuid, label: `Version ${row.sourceVersion}` }))}
          style={{ width: 230 }}
        />
        <Button disabled={!compareUuid} loading={compareLoading} onClick={compare}>
          Comparer à la version affichée
        </Button>
      </Space>
      {compareError && <Alert type="error" message={compareError} />}
      <Typography.Title level={4}>Historique conservé des sauvegardes</Typography.Title>
      <ResourceState {...records} retry={records.reload}>
        <Table<CatalogHistory>
          rowKey="externalId"
          dataSource={records.data?.items ?? []}
          pagination={{
            current: recordPage,
            pageSize: 25,
            total: records.data?.total,
            onChange: setRecordPage,
          }}
          columns={[
            {
              title: "Enregistré le",
              render: (_value, row) => dateValue(row.metadata.savedAt, true),
            },
            { title: "Motif", render: (_value, row) => textValue(row.metadata.reason) },
            {
              title: "Révision",
              render: (_value, row) => (
                <Link to={`/regulatory/templates/${templateUuid}/revisions/${row.revisionUuid}`}>
                  Consulter
                </Link>
              ),
            },
          ]}
          expandable={{ expandedRowRender: (row) => <JsonDetails value={row.metadata} /> }}
        />
      </ResourceState>
      <Modal
        title="Comparaison des définitions"
        open={!!comparison}
        width={1100}
        footer={null}
        onCancel={() => setComparison(null)}
        destroyOnClose
      >
        {comparison && (
          <RevisionComparison
            before={comparison.definition}
            after={selected.definition}
            beforeIncludes={comparison.includes}
            afterIncludes={selected.includes}
          />
        )}
      </Modal>
    </Space>
  )
}
export function TemplateDetailPage() {
  const { templateUuid, revisionUuid } = useParams<{
    templateUuid: string
    revisionUuid?: string
  }>()
  const history = useHistory()
  const { user } = useAuthContext()
  const { message, modal } = App.useApp()
  const resource = useCatalogResource(
    useCallback(
      async (signal) => {
        const detail = await regulatoryApi.template(templateUuid, signal)
        if (revisionUuid && revisionUuid !== detail.revision.uuid)
          return {
            ...detail,
            revision: await regulatoryApi.revision(templateUuid, revisionUuid, signal),
          }
        return detail
      },
      [templateUuid, revisionUuid],
    ),
  )
  const [cloneOpen, setCloneOpen] = useState(false)
  const [cloneName, setCloneName] = useState("")
  const [saving, setSaving] = useState(false)
  const [cloneError, setCloneError] = useState<string>()
  const detail = resource.data
  const permissions = catalogPermissions(user, detail?.template)
  const historical = !!detail && detail.revision.uuid !== detail.template.currentRevisionUuid
  const clone = async () => {
    if (!cloneName.trim()) {
      setCloneError("Le nom est requis.")
      return
    }
    setSaving(true)
    setCloneError(undefined)
    try {
      const result = await regulatoryApi.clone(
        templateUuid,
        cloneName.trim(),
        detail?.revision.uuid,
      )
      setCloneOpen(false)
      history.push(`/regulatory/templates/${result.templateUuid}`)
    } catch (error) {
      setCloneError(error instanceof Error ? error.message : "Duplication impossible")
    } finally {
      setSaving(false)
    }
  }
  const archive = () => {
    if (!detail?.template.currentRevisionUuid) return
    const archived = !detail.template.archived
    modal.confirm({
      title: archived ? "Archiver ce modèle ?" : "Restaurer ce modèle ?",
      content: "Les révisions historiques sont conservées.",
      okText: archived ? "Archiver" : "Restaurer",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await regulatoryApi.archive(templateUuid, archived, detail.template.currentRevisionUuid!)
          resource.reload()
          message.success(archived ? "Modèle archivé" : "Modèle restauré")
        } catch (error) {
          message.error(error instanceof Error ? error.message : "Opération impossible")
          throw error
        }
      },
    })
  }
  return (
    <CatalogFrame
      title={detail?.revision.definition.name ?? "Modèle de contrôle"}
      extra={
        <>
          <Button icon={<ReloadOutlined />} onClick={resource.reload}>
            Actualiser
          </Button>
          {permissions.manage && (
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                setCloneName(`${detail?.revision.definition.name ?? "Modèle"} — copie`)
                setCloneError(undefined)
                setCloneOpen(true)
              }}
            >
              Dupliquer pour mon entreprise
            </Button>
          )}
          {permissions.edit && !historical && !detail?.template.archived && (
            <Link to={`/regulatory/templates/${templateUuid}/edit`}>
              <Button type="primary" icon={<EditOutlined />}>
                Nouvelle révision
              </Button>
            </Link>
          )}
          {permissions.edit && !historical && (
            <Button onClick={archive}>
              {detail?.template.archived ? "Restaurer" : "Archiver"}
            </Button>
          )}
        </>
      }
    >
      <ResourceState {...resource} retry={resource.reload}>
        {detail && (
          <>
            <Space wrap>
              <ScopeTag scope={detail.revision.definition.scope} />
              <Tag>Version {detail.revision.sourceVersion}</Tag>
              <ReviewTag validated={detail.revision.definition.validated === true} />
              <OwnershipTag template={detail.template} />
              {detail.template.archived && <Tag>Archivé</Tag>}
            </Space>
            {historical && (
              <Alert
                showIcon
                type="info"
                message="Révision historique en lecture seule"
                description={
                  <Link to={`/regulatory/templates/${templateUuid}`}>
                    Voir la version actuelle ({detail.template.sourceVersion})
                  </Link>
                }
              />
            )}
            <Card>
              <Tabs
                key={detail.revision.uuid}
                items={[
                  {
                    key: "summary",
                    label: "Présentation",
                    children: (
                      <Descriptions
                        bordered
                        column={{ xs: 1, md: 2 }}
                        items={[
                          {
                            key: "description",
                            label: "Description",
                            children: (
                              <span className="regulatory-text">
                                {textValue(detail.revision.definition.description)}
                              </span>
                            ),
                            span: 2,
                          },
                          {
                            key: "reference",
                            label: "Référence",
                            children: textValue(detail.revision.definition.reference),
                            span: 2,
                          },
                          {
                            key: "coverage",
                            label: "Couverture",
                            children: textValue(detail.revision.definition.coverage),
                            span: 2,
                          },
                          {
                            key: "missing",
                            label: "Parties manquantes",
                            children:
                              detail.revision.definition.missingParts?.join(" ; ") ||
                              "Non renseigné",
                            span: 2,
                          },
                          {
                            key: "reviewer",
                            label: "Revu par",
                            children: textValue(detail.revision.definition.reviewer),
                          },
                          {
                            key: "date",
                            label: "Date de revue",
                            children: dateValue(detail.revision.definition.reviewDate),
                          },
                          {
                            key: "own",
                            label: "Points propres",
                            children: detail.revision.points.length,
                          },
                          {
                            key: "includes",
                            label: "Modules inclus",
                            children: detail.revision.includes.length,
                          },
                          {
                            key: "pack",
                            label: "Paquet source",
                            children: detail.revision.definition.sourcePack ? (
                              <Link
                                to={`/regulatory/sources?tab=packs&q=${encodeURIComponent(detail.revision.definition.sourcePack)}`}
                              >
                                {detail.revision.definition.sourcePack}
                              </Link>
                            ) : (
                              "Non renseigné"
                            ),
                            span: 2,
                          },
                        ]}
                      />
                    ),
                  },
                  {
                    key: "points",
                    label: "Points propres",
                    children: <PointTable points={detail.revision.points} />,
                  },
                  {
                    key: "includes",
                    label: "Modules inclus",
                    children: <><Alert type="info" message="Composants et constats communs" description="Une même grille peut être affectée à plusieurs composants, chacun avec ses réponses. Le partage des modules communs doit être configuré explicitement dans l’équipement avec des révisions identiques."/><IncludesPanel revision={detail.revision} /></>,
                  },
                  {
                    key: "resolved",
                    label: "Grille complète",
                    children: <ResolvedPanel revision={detail.revision} />,
                  },
                  {
                    key: "presentation",
                    label: "Catégories et icônes",
                    children: (
                      <PresentationEditor
                        revision={detail.revision}
                        editable={permissions.edit && !detail.template.archived}
                      />
                    ),
                  },
                  {
                    key: "history",
                    label: "Historique",
                    children: (
                      <HistoryPanel templateUuid={templateUuid} selected={detail.revision} />
                    ),
                  },
                ]}
              />
            </Card>
          </>
        )}
      </ResourceState>
      <Modal
        title="Dupliquer le modèle"
        open={cloneOpen}
        onCancel={() => setCloneOpen(false)}
        onOk={clone}
        confirmLoading={saving}
        okText="Dupliquer"
        cancelText="Annuler"
      >
        <Typography.Paragraph>
          La copie appartient à votre entreprise. Elle conserve les sources et les modules de la
          révision affichée et commence en version 1, sans revue héritée.
        </Typography.Paragraph>
        <Input
          aria-label="Nom de la copie"
          value={cloneName}
          onChange={(event) => setCloneName(event.target.value)}
        />
        {cloneError && (
          <Alert type="error" showIcon message={cloneError} style={{ marginTop: 12 }} />
        )}
      </Modal>
    </CatalogFrame>
  )
}
